import React, { useState, useEffect, useCallback } from 'react';
import { Overlay, OverlayProps } from '../overlay';
import { Tooltip, TooltipProps } from './tooltip';
import { Placement, TriggerType } from '../enums';
import { generateTooltipId } from '../helpers';
import { tooltipManager } from './tooltip-manager';

type OverlayTooltipProps = Omit<
  OverlayProps,
  'overlay' | 'trigger' | 'show' | 'onToggle'
> &
  Partial<Pick<TooltipProps, 'id'>> &
  Pick<TooltipProps, 'text' | 'rePositionOnChange' | 'className'> & {
    /**
     * If true, ensures only one tooltip is visible at a time.
     * When a new tooltip opens, any previously open tooltip will be closed.
     * Default: false (multiple tooltips can be visible simultaneously)
     */
    exclusive?: boolean;
  };

export const OverlayTooltip: React.FC<OverlayTooltipProps> = ({
  id: _id,
  placement,
  text,
  rePositionOnChange,
  className,
  children,
  exclusive = false,
  ...rest
}) => {
  const [id] = useState(_id || generateTooltipId());
  const [show, setShow] = useState(false);

  // Register/unregister with tooltip manager (only when exclusive is true)
  useEffect(() => {
    if (!exclusive) return;

    const closeCallback = () => {
      setShow(false);
    };
    tooltipManager.register(id, closeCallback);

    // eslint-disable-next-line consistent-return
    return () => {
      tooltipManager.unregister(id);
    };
  }, [id, exclusive]);

  // Handle tooltip toggle (only when exclusive is true)
  const handleToggle = useCallback(
    (nextShow: boolean) => {
      if (!exclusive) return;

      if (nextShow) {
        // Opening: notify manager to close other tooltips
        tooltipManager.open(id);
        setShow(true);
      } else {
        // Closing: notify manager
        tooltipManager.close(id);
        setShow(false);
      }
    },
    [id, exclusive],
  );

  const tooltip = (
    <Tooltip
      id={id}
      text={text}
      rePositionOnChange={rePositionOnChange}
      className={className}
    />
  );

  // Use controlled behavior when exclusive, otherwise use default behavior
  const overlayProps = exclusive
    ? {
        show,
        onToggle: handleToggle,
      }
    : {};

  return (
    <Overlay
      {...rest}
      {...overlayProps}
      placement={placement}
      overlay={tooltip}
      trigger={[TriggerType.Hover, TriggerType.Focus, TriggerType.Click]}
    >
      {children}
    </Overlay>
  );
};

OverlayTooltip.defaultProps = {
  placement: Placement.Bottom,
};
